import { MsalService } from '@azure/msal-angular';
import { EntraService, EntraUserAccess } from './entra.service';

/**
 * The signed-in user's app roles and groups, or `null` when they could not be determined — either
 * because MSAL has no account yet or because the probe failed. Callers must treat `null` as "unknown"
 * and neither block nor reroute on it.
 *
 * The token's `roles` claim wins when it carries anything, since it is already in hand. It says
 * nothing about group membership, which is fine: a user with app roles in their token is past the
 * point where the generic registrant group matters.
 */
export async function resolveUserAccess(
  msalService: MsalService,
  entraService: EntraService
): Promise<EntraUserAccess | null> {
  const account = msalService.instance.getActiveAccount() ?? msalService.instance.getAllAccounts()[0];
  if (!account) {
    return null;
  }

  const claimRoles = account.idTokenClaims?.['roles'] as string[] | undefined;
  if (claimRoles?.length) {
    return { appRoles: claimRoles, groups: [] };
  }

  // An absent OR empty roles claim falls through to Graph. Empty is not the same as "no roles": the
  // claim is omitted or blank whenever the app registration is not the one holding the assignment.
  return entraService.getEntraUserAccess();
}

/**
 * Case-insensitive membership test. Entra role names reach us by several routes — token claim, app
 * role Value, app role DisplayName — that do not agree on casing, and a case-sensitive compare here
 * reads as "wrong roles" and denies the user.
 */
export function hasAnyOf(held: readonly string[], wanted: readonly string[]): boolean {
  if (held.length === 0 || wanted.length === 0) {
    return false;
  }

  const normalized = new Set(wanted.map((value) => value.toLowerCase()));
  return held.some((value) => normalized.has(value.toLowerCase()));
}

/** Whether every role held is one of `wanted`. Vacuously true when the user holds none. */
export function holdsOnly(held: readonly string[], wanted: readonly string[]): boolean {
  const normalized = new Set(wanted.map((value) => value.toLowerCase()));
  return held.every((value) => normalized.has(value.toLowerCase()));
}
